import { SEARCH_HIGHLIGHT_KEY } from '@/constants/commons'
import type { ObjectDirective, DirectiveHook } from 'vue'

export function useTextHighlight() {
  const highlight = ref<Highlight | null>(null)

  const { isSafari, isIos } = useDevice()
  const isCSSSupported = useSupported(() => CSS?.highlights ?? false)
  const isSupported = computed(() => isCSSSupported.value && !isSafari && !isIos)

  tryOnBeforeMount(() => {
    if (!isSupported.value) return
    initCSSHighlight()
  })

  tryOnBeforeUnmount(() => {
    clear()
  })

  // * Highlight key name is constant now. It can be changed to a parameter in the future if needed. It depends on the CSS ::highlight API
  function initCSSHighlight() {
    highlight.value = CSS.highlights.get(SEARCH_HIGHLIGHT_KEY) ?? new Highlight()
    if (CSS.highlights.has(SEARCH_HIGHLIGHT_KEY)) return
    CSS.highlights.set(SEARCH_HIGHLIGHT_KEY, highlight.value as Highlight)
  }

  // [text, keyword]
  const vHighlight: ObjectDirective<HTMLElement, [string, string]> = {
    mounted(...params) {
      checkDirective(...params)
    },
    updated(...params) {
      checkDirective(...params)
    },
  }

  const checkDirective: DirectiveHook<HTMLElement, VNode<any, Element> | null, [string, string]> = (el, binding) => {
    const [text, keyword] = binding.value
    el.innerHTML = text || el.innerHTML
    apply(el, keyword)
  }

  // * Color is constant now. It can be changed to a parameter in the future if needed
  function apply(target: Element, keyword: string) {
    try {
      if (!keyword) return
      keyword = keyword.trim()
      if (!isSupported.value) {
        applyFallback(target, keyword)
        return
      }
      const textNode = target.firstChild!
      const startIndex = textNode.textContent!.toLowerCase().indexOf(keyword.toLowerCase())
      if (startIndex === -1) return
      const highlightRange = new Range()
      highlightRange.setStart(textNode!, startIndex)
      highlightRange.setEnd(textNode!, startIndex + keyword.length)
      highlight.value?.add(highlightRange)
    } catch {
      applyFallback(target, keyword)
    }
  }

  function applyFallback(target: Element, keyword: string) {
    target.innerHTML = target.innerHTML.replace(new RegExp(keyword, 'i'), '<span class="search-highlight">$&</span>')
  }

  function clear() {
    if (!isSupported.value) return
    highlight.value?.clear()
  }

  return {
    isSupported,
    clear,
    apply,
    /** vue custom directive */
    vHighlight,
  }
}
